import { useRef, useEffect, useState } from 'react';
import { ExperienceCard } from './ExperienceCard';
import { preloadThemeVideos } from '../../lib/videoPreloader';
import { getOptimizedImageUrl } from '../../lib/optimizedAssets';
import { useAnalyticsTracking } from '../../hooks/useAnalyticsTracking';

type ExperienceSize = 'lg' | 'md' | 'xl' | 'sm';

interface Experience {
  gifDark: string;
  gifLight: string;
  videoKey?: string; // Optional: key for optimized video
  title: string;
  description: string;
  size: ExperienceSize;
}

export function ExperienceSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { trackSectionView } = useAnalyticsTracking();

  // Animation phases for staggered entrance
  const [animationPhase, setAnimationPhase] = useState(0);
  const [isDark, setIsDark] = useState(true);

  // Detect theme change and initialize
  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // Initial check
    checkDarkMode();

    // Set up listeners for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.attributeName === 'class' &&
          mutation.target === document.documentElement
        ) {
          checkDarkMode();
        }
      });
    });

    // Start observing document for class changes
    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Track section view
            trackSectionView('Experience Section', {
              section_name: 'experience',
              section_position: 'top',
              visibility_percentage: Math.round(entry.intersectionRatio * 100)
            });
            
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [trackSectionView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          // Preload videos for current theme
          const videoUrls = experiences.map(exp => ({
            dark: exp.gifDark,
            light: exp.gifLight
          }));
          preloadThemeVideos(videoUrls, isDark);

          // Trigger staggered animations - with faster timing
          setTimeout(() => setAnimationPhase(1), 50);  // Section title - reduced from 100ms
          setTimeout(() => setAnimationPhase(2), 200); // Description - reduced from 400ms
          setTimeout(() => setAnimationPhase(3), 350); // First column - reduced from 700ms
          setTimeout(() => setAnimationPhase(4), 450); // Second column - reduced from 900ms

          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Balanced trigger for preloading
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isDark]);

  const experiences: Experience[] = [
    {
      gifDark: getOptimizedImageUrl("mocktrail-schdint-darkui.webp"),
      gifLight: getOptimizedImageUrl("mocktrail-schdint-lightui.webp"),
      // videoKey: "schedule",
      title: "Practice now or Schedule for later",
      description: "You can schedule your practice interviews to your Google Calendar, get reminders & practice at your convenience.",
      size: "lg"
    },
    {
      gifDark: getOptimizedImageUrl("mocktrail-camera-v2-darkui.webp"),
      gifLight: getOptimizedImageUrl("mocktrail-camera-v2-lightui.webp"),
      // videoKey: "camera",
      title: "Smart Video-Cam Capturing",
      description: "Our AI Interviewer will see your video camera and analyze your body language, tone and communication. Like an actual interviewer.",
      size: "md"
    },
    {
      gifDark: getOptimizedImageUrl("mocktrail-screen-darkui.webp"),
      gifLight: getOptimizedImageUrl("mocktrail-screen-lightui.webp"),
      title: "Screen-Share & Present",
      description: "Show your work to AI Interviewer, be it the given task, DSA solution or something interesting to show, MockTrail will capture it all.",
      size: "xl"
    },
    {
      gifDark: getOptimizedImageUrl("mocktrail-feedbkmodal-darkui.webp"),
      gifLight: getOptimizedImageUrl("mocktrail-feedbkmodal-lightui.webp"),
      title: "Detailed & Constructive Feedback",
      description: "Get detailed feedback for your performance, your ability to answer the question, your communication skills, and much more.",
      size: "md"
    }
  ];

  return (
    <section
      ref={sectionRef}
      className="py-32 relative overflow-hidden"
    >
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-light/5 to-transparent dark:via-primary-dark/5 opacity-0 transition-opacity duration-700"
        style={{ opacity: isVisible ? 1 : 0 }} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(37,99,235,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)] opacity-0 transition-opacity duration-700 delay-100 parallax-element"
        data-speed="0.05"
        style={{ opacity: isVisible ? 1 : 0 }} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(16,185,129,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_50%)] opacity-0 transition-opacity duration-700 delay-200 parallax-element"
        data-speed="0.08"
        style={{ opacity: isVisible ? 1 : 0 }} />

      {/* Floating particles */}
      <div className="absolute left-[15%] top-[20%] w-6 h-6 rounded-full bg-primary-light/15 dark:bg-primary-dark/15 blur-md animate-float opacity-0 transition-opacity duration-700 delay-150 parallax-element"
        data-speed="0.12"
        style={{ opacity: isVisible ? 0.7 : 0, animationDelay: '0.3s', animationDuration: '6s' }} />
      <div className="absolute right-[20%] top-[40%] w-4 h-4 rounded-full bg-secondary-light/15 dark:bg-secondary-dark/15 blur-md animate-float opacity-0 transition-opacity duration-700 delay-250 parallax-element"
        data-speed="0.16"
        style={{ opacity: isVisible ? 0.6 : 0, animationDelay: '0.5s', animationDuration: '5s' }} />

      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-20">
          <h2
            className={`text-3xl md:text-4xl font-bold mb-6 text-text-primary-light dark:text-text-primary-dark transform transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
          >
            Reject the Rejections! You're Here to Get Hired!
          </h2>
          <p
            className={`text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto transform transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
          >
            Enough of Rejections! Your interview preparation deserves the best planning. Know exactly how to improve with AI Interviews. <span className="text-text-primary-light dark:text-text-primary-dark">No `guesswork`.</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-6xl mx-auto">
          <div
            className={`space-y-4 transform transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 3 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
          >
            <div className="h-[350px]">
              <ExperienceCard {...experiences[0]} isDark={isDark} />
            </div>
            <div className="h-[400px] transform transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] delay-100">
              <ExperienceCard {...experiences[2]} isDark={isDark} />
            </div>
          </div>
          <div
            className={`space-y-4 md:mt-12 transform transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${animationPhase >= 4 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
              }`}
          >
            <div className="h-[400px]">
              <ExperienceCard {...experiences[1]} isDark={isDark} />
            </div>
            <div className="h-[350px] transform transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] delay-100">
              <ExperienceCard {...experiences[3]} isDark={isDark} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}